import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Calendar } from '@/components/ui/shadcn/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { cn, formatDate } from '@/lib/utils';
import { Calendar as CalendarIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

interface SetEstimatedDateSectionProps {
    estimatedDate?: string | null;
    onSetDate: (date: string) => void;
    readOnly?: boolean;
}

export function SetEstimatedDateSection({ estimatedDate, onSetDate, readOnly = false }: SetEstimatedDateSectionProps) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(estimatedDate ? new Date(estimatedDate) : undefined);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        setSelectedDate(estimatedDate ? new Date(estimatedDate) : undefined);
    }, [estimatedDate]);

    const handleSelectDate = (date?: Date) => {
        if (!date) return;
        setSelectedDate(date);
        onSetDate(formatDate(date));
        setIsOpen(false);
    };

    const renderDisplay = () => (
        <Button
            type="button"
            variant="ghost"
            className={cn(
                'flex w-full items-center justify-start gap-2 rounded px-2 py-1 text-left transition-colors',
                !readOnly && 'hover:bg-accent',
            )}
        >
            <CalendarIcon className="text-muted-foreground h-4 w-4" />
            {estimatedDate ? (
                <span className="text-sm font-medium">{formatDate(estimatedDate)}</span>
            ) : (
                <span className="text-muted-foreground text-sm italic">
                    {t('work_orders.show.sidebar.no_date_provided', 'No estimated date set')}
                </span>
            )}
        </Button>
    );

    return (
        <div className="space-y-1">
            <label className="text-muted-foreground text-sm">{t('work_orders.tables.estimated_date', 'Estimated Date')}</label>
            {readOnly ? (
                renderDisplay()
            ) : (
                <>
                    {/* Anchored to a row: a dropdown on desktop, so the mobile branch
                        stays a bottom drawer rather than a sheet. */}
                    {isMobile ? (
                        <ResponsiveModal open={isOpen} onOpenChange={setIsOpen} variant="dialog">
                            <ResponsiveModalTrigger asChild>{renderDisplay()}</ResponsiveModalTrigger>
                            <ResponsiveModalContent dialogClassName="sm:max-w-md">
                                <ResponsiveModalHeader>
                                    <ResponsiveModalTitle>{t('work_orders.tables.estimated_date', 'Estimated Date')}</ResponsiveModalTitle>
                                </ResponsiveModalHeader>
                                <ResponsiveModalBody className="flex justify-center">
                                    <Calendar mode="single" selected={selectedDate} onSelect={handleSelectDate} initialFocus />
                                </ResponsiveModalBody>
                            </ResponsiveModalContent>
                        </ResponsiveModal>
                    ) : (
                        <Popover open={isOpen} onOpenChange={setIsOpen}>
                            <PopoverTrigger asChild>{renderDisplay()}</PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar mode="single" selected={selectedDate} onSelect={handleSelectDate} initialFocus />
                            </PopoverContent>
                        </Popover>
                    )}
                </>
            )}
        </div>
    );
}
